/**
 * Puente de impresión local vía **QZ Tray** (agente en la máquina del POS): el navegador
 * habla por websocket con `localhost` y QZ manda RAW ESC/POS a la impresora — impresión
 * silenciosa, sin diálogo del navegador, y alcanza la LAN del local (el backend en la
 * nube no podría). Setup por cliente: instalar QZ Tray + (opcional) certificado firmado.
 *
 * Carga `qz-tray.js` de forma diferida (env `VITE_QZ_TRAY_URL`, default CDN jsDelivr; en
 * producción conviene auto-hospedarlo: copiar a `public/qz-tray.js` y apuntar la env).
 *
 * Firma: si se configura un certificado (`VITE_QZ_CERT` / `VITE_QZ_CERT_URL`) + endpoint
 * de firma (`VITE_QZ_SIGN_URL`), se firman las peticiones (modo "trusted", sin prompts).
 * Sin certificado, QZ corre en modo NO firmado y muestra su diálogo "Allow" una vez por
 * sesión (el usuario puede marcar "Remember"). Ver docs/STRATOS_CONTEXT (cómo generar
 * el cert por cliente).
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window { qz?: any }
}

const QZ_URL = (import.meta as any).env?.VITE_QZ_TRAY_URL
  ?? 'https://cdn.jsdelivr.net/npm/qz-tray@2.2.4/qz-tray.js';
const QZ_CERT = (import.meta as any).env?.VITE_QZ_CERT as string | undefined;
const QZ_CERT_URL = (import.meta as any).env?.VITE_QZ_CERT_URL as string | undefined;
const QZ_SIGN_URL = (import.meta as any).env?.VITE_QZ_SIGN_URL as string | undefined;

let scriptPromise: Promise<any> | null = null;
let securityConfigured = false;

/** Inyecta qz-tray.js una sola vez y resuelve con el objeto global `qz`. */
function loadScript(): Promise<any> {
  if (window.qz) return Promise.resolve(window.qz);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = QZ_URL;
    s.async = true;
    s.onload = () => (window.qz ? resolve(window.qz) : reject(new Error('qz-tray.js cargó pero no expuso `qz`')));
    s.onerror = () => reject(new Error(`No se pudo cargar qz-tray.js desde ${QZ_URL}`));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

async function resolveCert(): Promise<string | null> {
  if (QZ_CERT) return QZ_CERT;
  if (QZ_CERT_URL) {
    try { const r = await fetch(QZ_CERT_URL); if (r.ok) return await r.text(); } catch { /* unsigned */ }
  }
  return null;
}

/** Configura los promises de certificado/firma (idempotente). Sin cert → modo no firmado. */
async function configureSecurity(qz: any): Promise<void> {
  if (securityConfigured) return;
  securityConfigured = true;
  const cert = await resolveCert();
  if (cert) {
    qz.security.setCertificatePromise((resolve: any) => resolve(cert));
  }
  if (cert && QZ_SIGN_URL) {
    qz.security.setSignatureAlgorithm?.('SHA512');
    qz.security.setSignaturePromise((toSign: string) => (resolve: any, reject: any) => {
      fetch(QZ_SIGN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ request: toSign }),
      })
        .then((r) => (r.ok ? r.text() : Promise.reject(new Error('firma rechazada'))))
        .then(resolve)
        .catch(reject);
    });
  } else {
    // Modo no firmado: resolver vacío para no bloquear (QZ pedirá "Allow" una vez).
    qz.security.setSignaturePromise(() => (resolve: any) => resolve());
  }
}

/** ¿Hay websocket activo con QZ Tray? (sin intentar conectar). */
export function isConnected(): boolean {
  return !!window.qz?.websocket?.isActive?.();
}

/** Carga el script, configura seguridad y abre el websocket. Lanza si QZ no está corriendo. */
export async function connect(): Promise<void> {
  const qz = await loadScript();
  await configureSecurity(qz);
  if (!qz.websocket.isActive()) await qz.websocket.connect();
}

/** Lista las impresoras visibles para QZ Tray (requiere conexión). */
export async function listPrinters(): Promise<string[]> {
  await connect();
  const found = await window.qz.printers.find();
  return Array.isArray(found) ? found : found ? [found] : [];
}

/** string latin1 (1 char = 1 byte) → hex, para enviar bytes EXACTOS (format:'hex'). */
function toHex(payload: string): string {
  let out = '';
  for (let i = 0; i < payload.length; i++) out += (payload.charCodeAt(i) & 0xff).toString(16).padStart(2, '0');
  return out;
}

/** Imprime un payload ESC/POS (string de bytes latin1) en `printer` vía QZ Tray RAW. */
export async function printEscPos(printer: string, payload: string): Promise<void> {
  await connect();
  const cfg = window.qz.configs.create(printer, { encoding: 'CP850' });
  await window.qz.print(cfg, [{ type: 'raw', format: 'hex', data: toHex(payload) }]);
}

// ---------------------------------------------------------------------------
// Selección de impresora por estación/tenant (persistente en localStorage).
// ---------------------------------------------------------------------------
const printerKey = (tenantId?: string) => `ag_stratos.qz.printer.${tenantId ?? 'default'}`;

export function getSelectedPrinter(tenantId?: string): string | null {
  try { return localStorage.getItem(printerKey(tenantId)); } catch { return null; }
}
export function setSelectedPrinter(tenantId: string | undefined, printer: string | null): void {
  try {
    if (printer) localStorage.setItem(printerKey(tenantId), printer);
    else localStorage.removeItem(printerKey(tenantId));
  } catch { /* storage no disponible */ }
}

/** ¿Hay flujo de firma configurado? (para mostrarlo en Configuración). */
export const isSignedMode = !!((QZ_CERT || QZ_CERT_URL) && QZ_SIGN_URL);
